/**
 * LumaScript Validator
 *
 * Validates LumaScript JSON against schema and business rules
 */

import { lumaScriptSchema } from './schemas';
import type { LumaScript, LumaNode, Connection } from '@luma-factory/shared';
import { ZodError } from 'zod';

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  code: string;
  message: string;
  path?: string;
  line?: number;
}

export interface ValidationWarning {
  code: string;
  message: string;
  path?: string;
}

/**
 * Validate LumaScript against schema and business rules
 */
export function validateLumaScript(input: unknown): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // 1. Schema validation
  try {
    const parsed = lumaScriptSchema.parse(input);

    // 2. Business rule validation
    validateBusinessRules(parsed, errors, warnings);

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  } catch (error) {
    if (error instanceof ZodError) {
      errors.push(...zodErrorsToValidationErrors(error));
    } else {
      errors.push({
        code: 'UNKNOWN_ERROR',
        message: error instanceof Error ? error.message : 'Unknown validation error'
      });
    }

    return {
      valid: false,
      errors,
      warnings
    };
  }
}

/**
 * Convert Zod errors to validation errors
 */
function zodErrorsToValidationErrors(zodError: ZodError): ValidationError[] {
  return zodError.errors.map(err => ({
    code: err.code.toUpperCase(),
    message: err.message,
    path: err.path.join('.')
  }));
}

/**
 * Validate business rules
 */
function validateBusinessRules(
  lumascript: LumaScript,
  errors: ValidationError[],
  warnings: ValidationWarning[]
): void {
  // Rule 1: Must have exactly one Start node
  const startNodes = lumascript.nodes.filter(n => n.type === 'luma.Start');
  if (startNodes.length === 0) {
    errors.push({
      code: 'MISSING_START_NODE',
      message: 'LumaScript must have exactly one Start node'
    });
  } else if (startNodes.length > 1) {
    errors.push({
      code: 'MULTIPLE_START_NODES',
      message: `Found ${startNodes.length} Start nodes, expected 1`
    });
  }

  // Rule 2: All connections must reference valid nodes and ports
  validateConnections(lumascript.nodes, lumascript.connections, errors);

  // Rule 3: No orphaned nodes (except Start)
  validateOrphanedNodes(lumascript.nodes, lumascript.connections, warnings);

  // Rule 4: No circular dependencies
  validateCircularDependencies(lumascript.nodes, lumascript.connections, errors);

  // Rule 5: All nodes must have unique IDs
  validateUniqueIds(lumascript.nodes, errors);

  // Rule 6: Validate node definitions against node type requirements
  validateNodeDefinitions(lumascript.nodes, errors, warnings);
}

/**
 * Validate connections reference valid nodes and ports
 */
function validateConnections(
  nodes: LumaNode[],
  connections: Connection[],
  errors: ValidationError[]
): void {
  const nodeIds = new Set(nodes.map(n => n.id));
  const portMap = new Map<string, Set<string>>();

  // Build port map
  for (const node of nodes) {
    if (node.ports) {
      portMap.set(node.id, new Set(node.ports.items.map(p => p.id)));
    }
  }

  for (const conn of connections) {
    // Validate source node exists
    if (!nodeIds.has(conn.source.cell)) {
      errors.push({
        code: 'INVALID_CONNECTION_SOURCE',
        message: `Connection ${conn.id} references non-existent source node ${conn.source.cell}`,
        path: `connections[${conn.id}].source.cell`
      });
    }

    // Validate target node exists
    if (!nodeIds.has(conn.target.cell)) {
      errors.push({
        code: 'INVALID_CONNECTION_TARGET',
        message: `Connection ${conn.id} references non-existent target node ${conn.target.cell}`,
        path: `connections[${conn.id}].target.cell`
      });
    }

    // Validate source port exists
    const sourcePorts = portMap.get(conn.source.cell);
    if (sourcePorts && !sourcePorts.has(conn.source.port)) {
      errors.push({
        code: 'INVALID_SOURCE_PORT',
        message: `Connection ${conn.id} references non-existent source port ${conn.source.port}`,
        path: `connections[${conn.id}].source.port`
      });
    }

    // Validate target port exists
    const targetPorts = portMap.get(conn.target.cell);
    if (targetPorts && !targetPorts.has(conn.target.port)) {
      errors.push({
        code: 'INVALID_TARGET_PORT',
        message: `Connection ${conn.id} references non-existent target port ${conn.target.port}`,
        path: `connections[${conn.id}].target.port`
      });
    }
  }
}

/**
 * Detect orphaned nodes (nodes with no incoming or outgoing connections)
 */
function validateOrphanedNodes(
  nodes: LumaNode[],
  connections: Connection[],
  warnings: ValidationWarning[]
): void {
  const connectedNodes = new Set<string>();

  for (const conn of connections) {
    connectedNodes.add(conn.source.cell);
    connectedNodes.add(conn.target.cell);
  }

  for (const node of nodes) {
    // Skip Start and End nodes
    if (node.type === 'luma.Start' || node.type === 'luma.End') {
      continue;
    }

    if (!connectedNodes.has(node.id)) {
      warnings.push({
        code: 'ORPHANED_NODE',
        message: `Node "${node.name}" (${node.id}) has no connections`,
        path: `nodes[${node.id}]`
      });
    }
  }
}

/**
 * Detect circular dependencies
 */
function validateCircularDependencies(
  nodes: LumaNode[],
  connections: Connection[],
  errors: ValidationError[]
): void {
  const adjacencyList = new Map<string, string[]>();

  // Build adjacency list
  for (const node of nodes) {
    adjacencyList.set(node.id, []);
  }

  for (const conn of connections) {
    const neighbors = adjacencyList.get(conn.source.cell);
    if (neighbors) {
      neighbors.push(conn.target.cell);
    }
  }

  // DFS to detect cycles
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  function hasCycle(nodeId: string): boolean {
    visited.add(nodeId);
    recursionStack.add(nodeId);

    const neighbors = adjacencyList.get(nodeId) || [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        if (hasCycle(neighbor)) {
          return true;
        }
      } else if (recursionStack.has(neighbor)) {
        return true;
      }
    }

    recursionStack.delete(nodeId);
    return false;
  }

  for (const node of nodes) {
    if (!visited.has(node.id)) {
      if (hasCycle(node.id)) {
        errors.push({
          code: 'CIRCULAR_DEPENDENCY',
          message: 'Circular dependency detected in node connections'
        });
        break;
      }
    }
  }
}

/**
 * Validate all node IDs are unique
 */
function validateUniqueIds(nodes: LumaNode[], errors: ValidationError[]): void {
  const ids = new Set<string>();
  const duplicates = new Set<string>();

  for (const node of nodes) {
    if (ids.has(node.id)) {
      duplicates.add(node.id);
    }
    ids.add(node.id);
  }

  for (const duplicateId of duplicates) {
    errors.push({
      code: 'DUPLICATE_NODE_ID',
      message: `Duplicate node ID: ${duplicateId}`,
      path: `nodes[${duplicateId}]`
    });
  }
}

/**
 * Validate node definitions against node type requirements
 */
function validateNodeDefinitions(
  nodes: LumaNode[],
  errors: ValidationError[],
  warnings: ValidationWarning[]
): void {
  for (const node of nodes) {
    // Type-specific validation
    switch (node.type) {
      case 'luma.Message':
        if (!node.definition?.message) {
          errors.push({
            code: 'MISSING_REQUIRED_FIELD',
            message: `Node "${node.name}" (${node.type}) requires a "message" field`,
            path: `nodes[${node.id}].definition.message`
          });
        }
        break;

      case 'luma.AITask':
        if (!node.definition?.promptDescription) {
          errors.push({
            code: 'MISSING_REQUIRED_FIELD',
            message: `Node "${node.name}" (${node.type}) requires a "promptDescription" field`,
            path: `nodes[${node.id}].definition.promptDescription`
          });
        }
        break;

      case 'luma.PromptAttribute':
        if (!node.definition?.attributeName) {
          errors.push({
            code: 'MISSING_REQUIRED_FIELD',
            message: `Node "${node.name}" (${node.type}) requires an "attributeName" field`,
            path: `nodes[${node.id}].definition.attributeName`
          });
        }
        break;

      case 'luma.Condition':
        if (!node.definition?.conditionalRules || !Array.isArray(node.definition.conditionalRules)) {
          errors.push({
            code: 'MISSING_REQUIRED_FIELD',
            message: `Node "${node.name}" (${node.type}) requires "conditionalRules" array`,
            path: `nodes[${node.id}].definition.conditionalRules`
          });
        }
        break;

      // Add more type-specific validations as needed
    }

    // Warn if definition is empty for non-Start/End nodes
    if (node.type !== 'luma.Start' && node.type !== 'luma.End') {
      if (!node.definition || Object.keys(node.definition).length === 0) {
        warnings.push({
          code: 'EMPTY_DEFINITION',
          message: `Node "${node.name}" (${node.type}) has an empty definition`,
          path: `nodes[${node.id}].definition`
        });
      }
    }
  }
}

/**
 * Quick validation - just checks if it's valid JSON and has basic structure
 */
export function quickValidate(input: unknown): boolean {
  try {
    lumaScriptSchema.parse(input);
    return true;
  } catch {
    return false;
  }
}
